import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlane,
  faUser,
  faCircleInfo,
  faBolt,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../../config";
import { useNavigate, useLocation } from "react-router-dom";
import ProgressBar from "../../components/hotels/ProgressBar";
import {
  DuffelCardForm,
  createThreeDSecureSession,
  useDuffelCardFormActions,
} from "@duffel/components";
import StayDetailsModal from "../../components/hotels/StayDetailsModal";

const formatTitle = (title) => {
  const titles = {
    mr: "Mr",
    mrs: "Mrs",
    ms: "Ms",
    miss: "Miss",
    dr: "Dr",
  };
  return titles[title.toLowerCase()] || title;
};

function HotelsBookingSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [contactDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [clientKey, setClientKey] = useState(null);
  const [isLoadingClientKey, setIsLoadingClientKey] = useState(false);
  const { ref, saveCard } = useDuffelCardFormActions();
  const [isFlightModalOpen, setIsFlightModalOpen] = useState(false);

  // Get Data
  let bookingData;
  try {
    const rawData = searchParams.get("data");
    bookingData = JSON.parse(decodeURIComponent(rawData));
    console.log("Data Received from BookingDetail:", bookingData);
  } catch (error) {
    console.error("Error parsing booking data:", error);
  }

  // Define Key Variables
  const flight = bookingData.selectedOffer;
  const passengers = bookingData.passengers;

  // Update price references
  const price = bookingData?.selectedOffer?.data?.total_amount || "0.00";
  const currency = bookingData?.selectedOffer?.data?.total_currency || "GBP";
  const baseAmount = bookingData?.selectedOffer?.data?.base_amount || "0.00";
  const taxAmount = bookingData?.selectedOffer?.data?.tax_amount || "0.00";

  // Payment Intent
  useEffect(() => {
    async function createPaymentIntent() {
      if (
        selectedPaymentMethod === "Credit Card" &&
        bookingData?.selectedOffer?.price
      ) {
        setIsLoadingClientKey(true);
        setPaymentError(null);

        try {
          const response = await fetch(
            `${config.backendServer}/api/payments/client-key`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                amount: bookingData.selectedOffer.price,
                currency: "GBP",
              }),
            }
          );

          if (!response.ok) {
            throw new Error("Failed to create payment intent");
          }

          const data = await response.json();
          console.log("Payment intent response:", data);

          const fetchedKey = data.clientToken;
          console.log("Client key received:", fetchedKey);

          if (!fetchedKey) {
            throw new Error("No client key received from server");
          }

          setClientKey(fetchedKey);
        } catch (error) {
          console.error("Error creating payment intent:", error);
          setPaymentError("Failed to initialize payment. Please try again.");
        } finally {
          setIsLoadingClientKey(false);
        }
      }
    }

    createPaymentIntent();
  }, [selectedPaymentMethod, bookingData?.selectedOffer?.price]);

  // Create Card
  const handleCreateCardSuccess = async (data) => {
    console.log("Card created successfully:", data);

    try {
      setIsProcessingPayment(true);
      setPaymentError(null);

      const cardId = data.id;
      console.log("Card ID:", cardId);

      const offerId = flight.data?.id || flight.id;
      console.log("Using offer ID:", offerId);

      const offerServices = [
        // Define any ancillary services here, e.g.,
        // { id: 'ase_00009UhD4ongolulWAAA1A', quantity: 1 }
      ];

      const threeDSecureSession = await createThreeDSecureSession(
        clientKey,
        cardId,
        offerId,
        offerServices,
        true // Redirect mode
      );

      console.log("3DSecureSession:", threeDSecureSession);

      if (threeDSecureSession.status === "ready_for_payment") {
        const payload = {
          selected_offers: [offerId],
          three_d_secure_session_id: threeDSecureSession.id,
          passengers: passengers.map((passenger) => ({
            id: passenger.passenger_id,
            title: passenger.title,
            given_name: passenger.firstName,
            family_name: passenger.lastName,
            born_on: passenger.dob,
            gender: passenger.gender === "male" ? "m" : "f",
            phone_number: passenger.phone.replace(/\s+/g, ""),
            email: passenger.email,
          })),
          amount: flight.price,
          currency: "GBP",
        };

        console.log("Creating order with payment:", payload);

        const orderResponse = await fetch(
          `${config.backendServer}/api/orders`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        const orderData = await orderResponse.json();

        if (!orderResponse.ok) {
          console.error("Failed to create order:", orderData);
          throw new Error(orderData.error?.message || "Failed to create order");
        }

        navigate(`/hotels/confirmation?order_id=${orderData.data.id}`);
      } else {
        console.warn(
          "3DS session status is not ready_for_payment, please try again",
          { threeDSecureSession }
        );
        setPaymentError("3D Secure authentication failed. Please try again.");
      }
    } catch (error) {
      console.error("Payment/Order error:", error);
      setPaymentError(
        error.message || "Failed to process payment. Please try again."
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Card Error
  const handleCreateCardFailure = (error) => {
    console.error("Failed to create card:", error);
    setPaymentError(
      error.message || "Failed to create card. Please try again."
    );
  };

  // Save Card
  const handleValidateSuccess = () => {
    console.log("Validation successful");
    saveCard();
  };

  // Continue
  const handleContinue = () => {
    const newErrors = {};
    if (!contactDetails.firstName)
      newErrors.firstName = "First name is required";
    if (!contactDetails.lastName) newErrors.lastName = "Last name is required";
    if (!selectedPaymentMethod) {
      newErrors.payment = "Please select a payment method";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Payment Methods
  const paymentMethods = [
    {
      name: "Credit Card",
      description: "Pay with your credit card",
      logo: "/credit-card-logo.png",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container pb-4 mx-auto px-4 md:px-40 lg:px-40">
        <div className="flex justify-between py-6 items-center border-b">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {flight.origin} <FontAwesomeIcon icon={faPlane} size="sm" />{" "}
              {flight.destination}
            </h1>
            <p className="text-gray-600">Fri, 14 Feb '25 • Sun, 16 Feb '25</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">£{flight.price}</p>
            <p className="text-gray-600">Total price</p>
          </div>
        </div>
        <ProgressBar currentStep={2} />

        {/* Flight Summary */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faPlane}
                className={`${config.primaryColorText}`}
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Your flights</h2>
            </div>
          </div>

          {/* Flight Details */}
          <div
            className="space-y-4 cursor-pointer"
            onClick={() => setIsFlightModalOpen(true)}
          >
            <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
              <button
                className="w-full flex justify-between items-center mb-6"
                onClick={() => setIsFlightModalOpen(true)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <img
                      src={flight.data?.owner.logo_symbol_url}
                      alt={flight.data?.owner.name}
                      className="w-10 h-10"
                    />
                  </div>
                  <div className="text-left">
                    <h2 className=" font-semibold">
                      {flight.data?.owner.name}
                    </h2>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-end gap-0 flex-col">
                    <p className="text-lg font-bold">£{flight.price}</p>
                    <p className="text-xs text-gray-500">Total Price</p>
                  </div>
                </div>
              </button>

              {/* Outbound Flight */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex flex-col items-start">
                  <span className="text-lg font-semibold">
                    {flight.outbound_time}
                  </span>
                  <span className=" text-gray-600">
                    {flight.data?.slices[0].origin.iata_code}
                  </span>
                </div>
                <div className="flex-1 px-8">
                  <div className="flex flex-col items-center">
                    <span className="text-gray-500 text-xs">
                      {flight.stops}
                    </span>
                    <div className="w-full flex items-center gap-2">
                      <div className="h-[2px] flex-1 bg-gray-300"></div>
                      <FontAwesomeIcon
                        icon={faPlane}
                        className="text-gray-400"
                      />
                      <div className="h-[2px] flex-1 bg-gray-300"></div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-lg font-semibold">
                    {flight.outbound_arrival_time}
                  </span>
                  <span className=" text-gray-600">
                    {flight.data?.slices[0].destination.iata_code}
                  </span>
                </div>
              </div>

              {/* Return Flight */}
              {flight.return_date && (
                <div className="flex items-center justify-between">
                  <div className="flex flex-col items-start">
                    <span className="text-lg font-semibold">
                      {flight.return_time}
                    </span>
                    <span className=" text-gray-600">
                      {flight.data?.slices[1].origin.iata_code}
                    </span>
                  </div>
                  <div className="flex-1 px-8">
                    <div className="flex flex-col items-center">
                      <span className="text-gray-500 text-xs">
                        {flight.stops}
                      </span>
                      <div className="w-full flex items-center gap-2">
                        <div className="h-[2px] flex-1 bg-gray-300"></div>
                        <FontAwesomeIcon
                          icon={faPlane}
                          className="text-gray-400"
                        />
                        <div className="h-[2px] flex-1 bg-gray-300"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-lg font-semibold">
                      {flight.return_arrival_time}
                    </span>
                    <span className=" text-gray-600">
                      {flight.data?.slices[1].destination.iata_code}
                    </span>
                  </div>
                </div>
              )}

              <div className="mt-6 text-sm flex justify-end">
                <button
                  className="bg-gray-100 text-gray-700 px-6 py-2 rounded-full flex items-center gap-2"
                  onClick={() => setIsFlightModalOpen(true)}
                >
                  Trip Details
                  <FontAwesomeIcon icon={faCopy} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Flight Details Modal */}
        <StayDetailsModal
          isOpen={isFlightModalOpen}
          onClose={() => setIsFlightModalOpen(false)}
          selectedOffer={bookingData?.selectedOffer}
        />

        {/* Passengers */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faUser}
                className={`${config.primaryColorText}`}
              />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Passengers</h2>
            </div>
          </div>

          <div className="space-y-4">
            {passengers.map((passenger, index) => (
              <div
                key={index}
                className="flex items-center gap-4 border p-2 rounded-lg"
              >
                <div className="bg-coral-100 rounded-full w-12 h-12 flex items-center justify-center text-xl font-semibold text-coral-600">
                  {passenger.firstName[0]}
                </div>
                <div>
                  <p className="font-semibold">
                    {formatTitle(passenger.title)}. {passenger.firstName}{" "}
                    {passenger.lastName}
                  </p>
                  {index === 0 && (
                    <>
                      <p className="text-gray-600">{passenger.email}</p>
                      <p className="text-gray-600">{passenger.phone}</p>
                    </>
                  )}
                  <p className="text-gray-600 capitalize">
                    {passenger.type?.replace(/_/g, " ")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Selection */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faBolt}
                  className={`${config.primaryColorText}`}
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Payment selection</h2>
              </div>
            </div>
          </div>

          {/* Payment Methods Selection */}
          <div className="border border-purple-200 rounded-lg overflow-hidden mb-4">
            <div className="p-4 bg-gray-50">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faBolt}
                  className={`${config.primaryColorText}`}
                />
                <span className="font-semibold">Select Payment Method</span>
              </div>
            </div>

            <div className="divide-y">
              {paymentMethods.map((method, index) => (
                <label
                  key={index}
                  className="flex items-center p-4 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="radio"
                    name="payment"
                    className="form-radio text-purple-600"
                    checked={selectedPaymentMethod === method.name}
                    onChange={() => setSelectedPaymentMethod(method.name)}
                  />
                  <div className="ml-4">
                    <p className="font-semibold">{method.name}</p>
                    <p className="text-gray-600 text-sm">
                      {method.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Payment Form Section */}
          {selectedPaymentMethod === "Credit Card" && (
            <div className="mt-4">
              {isLoadingClientKey ? (
                <div className="text-gray-600">
                  Initializing payment form...
                </div>
              ) : clientKey ? (
                <>
                  {/* Debug section to show client key */}
                  <div className="mb-4 p-4 bg-gray-100 rounded-lg">
                    <p className="text-sm font-mono break-all">
                      <strong>Under Construction:</strong> Payments are not
                      active on this demo
                    </p>
                  </div>

                  <DuffelCardForm
                    ref={ref}
                    clientKey={clientKey}
                    intent="to-save-card"
                    onSaveCardSuccess={handleCreateCardSuccess}
                    onSaveCardFailure={handleCreateCardFailure}
                    onValidateSuccess={handleValidateSuccess}
                    onValidateFailure={() => console.log("Validation failed")}
                    onError={(error) => {
                      console.error("Payment form error:", error);
                      setPaymentError(
                        error.message || "Payment failed. Please try again."
                      );
                    }}
                  />
                </>
              ) : paymentError ? (
                <div className="text-red-600">{paymentError}</div>
              ) : null}

              {paymentError && !isLoadingClientKey && (
                <div className="mt-2 text-red-600">{paymentError}</div>
              )}
              {isProcessingPayment && (
                <div className="mt-2 text-gray-600">Processing payment...</div>
              )}
            </div>
          )}

          {errors.payment && (
            <div className="mt-2 text-red-600">{errors.payment}</div>
          )}
        </div>

        {/* Price Breakdown */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={faCircleInfo}
                className={`${config.primaryColorText}`}
              />
            </div>
            <h2 className="text-lg font-semibold">Price breakdown</h2>
          </div>

          <div className="space-y-4">
            {/* Passenger Count Summary */}
            <div className="pb-4 border-b border-gray-200">
              <h3 className="font-medium mb-2">Passengers</h3>
              {bookingData?.selectedOffer?.data?.passengers?.length > 0 && (
                <div className="text-gray-600">
                  {Object.entries(
                    bookingData.selectedOffer.data.passengers.reduce(
                      (acc, passenger) => {
                        acc[passenger.type] = (acc[passenger.type] || 0) + 1;
                        return acc;
                      },
                      {}
                    )
                  ).map(([type, count]) => (
                    <div key={type} className="flex justify-between">
                      <span className="capitalize">
                        {type.replace(/_/g, " ")}
                      </span>
                      <span>x{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Base Fare */}
            <div className="pb-4 border-b border-gray-200">
              <div className="flex justify-between">
                <span>Base fare</span>
                <span>£{baseAmount}</span>
              </div>
            </div>

            {/* Taxes and Fees */}
            <div className="pb-4 border-b border-gray-200">
              <div className="flex justify-between">
                <span>Taxes and fees</span>
                <span>£{taxAmount}</span>
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-between font-bold text-lg pt-2">
              <span>Total</span>
              <span>£{price}</span>
            </div>

            {/* Additional Info */}
            <div className="mt-4 text-sm text-gray-500">
              <p>* All prices are in {currency}</p>
              {bookingData?.selectedOffer?.data?.total_emissions_kg && (
                <p className="mt-2">
                  Estimated CO₂ emissions:{" "}
                  {bookingData.selectedOffer.data.total_emissions_kg}
                  kg
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={handleContinue}
            disabled={isProcessingPayment}
            className={`px-6 py-3 ${config.primaryColorBg} text-white rounded-lg hover:bg-purple-700 disabled:opacity-50`}
          >
            {isProcessingPayment ? "Processing..." : "Continue to Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default HotelsBookingSummary;
